import type { ReactNode } from 'react';
import { monthLabel } from '@core/dates';
import { HEADER_HEIGHT, HEADER_ROWS, MIN_MONTH_LABEL, periodSpans, type Grid } from './geometry';

const MIN_QUARTER_LABEL = 80; // уже — подпись квартала не помещается
const DAY_LINE = '#e5e7eb';   // линии дней в дневном режиме (gray-200)

// Угол над колонкой названий: одна ячейка высотой в три строки шапки.
export function GridCorner({ width, label, extra }: { width: number; label: string; extra?: ReactNode }) {
  return (
    <div className="shrink-0 bg-gray-100 border-r-2 border-gray-300" style={{ width }}>
      <div className="px-3 flex items-center" style={{ height: HEADER_HEIGHT }}>
        <span className="font-semibold">{label}</span>
      </div>
      {extra}
    </div>
  );
}

// Шапка сетки как в старом роадмапе: кварталы с годом, месяцы, понедельники недель.
// Кварталы и месяцы — по реальным датам, недели — по понедельникам; у обрезанной первой недели подписи нет.
export function GridHeader({ grid }: { grid: Grid }) {
  const quarters = periodSpans(grid, 'quarter');
  const months = periodSpans(grid, 'month');
  return (
    <div style={{ width: grid.width }}>
      <div className="relative bg-blue-50 border-b border-gray-300" style={{ height: HEADER_ROWS.quarters }}>
        {quarters.map((q) => (
          <span key={q.key} className="absolute top-0 bottom-0 flex items-center justify-center font-semibold border-r border-gray-300 overflow-hidden whitespace-nowrap" style={{ left: q.left, width: q.width }} title={q.label}>{q.width >= MIN_QUARTER_LABEL ? q.label : ''}</span>
        ))}
      </div>
      <div className="relative bg-gray-50 border-b border-gray-300" style={{ height: HEADER_ROWS.months }}>
        {months.map((m) => (
          <span key={m.key} className="absolute top-0 bottom-0 flex items-center justify-center text-sm font-medium border-r border-gray-300 overflow-hidden whitespace-nowrap" style={{ left: m.left, width: m.width }} title={m.label}>{m.width >= MIN_MONTH_LABEL ? monthLabel(m.key) : ''}</span>
        ))}
      </div>
      <div className="relative bg-gray-50 border-b border-gray-200" style={{ height: HEADER_ROWS.weeks }}>
        {grid.columns.map((c) => (
          <span key={c.monday} className="absolute top-0 bottom-0 flex items-center text-xs pl-1 border-r border-gray-200" style={{ left: c.left, width: 7 * grid.dayWidth }}>
            {c.left >= 0 && c.day}
          </span>
        ))}
      </div>
    </div>
  );
}

// Фон любой строки сетки: линии недель и, заметнее, месяцев (квартал — обычная смена месяца: квартального
// планирования нет), полоса «сегодня» шириной в день и, в дневном режиме, линии дней. todayX — левый край дня.
export function GridLines({ grid, todayX, days, holidays = [], spot }: { grid: Grid; todayX: number | null; days?: boolean; holidays?: number[]; spot?: { left: number; width: number } | null }) {
  const months = periodSpans(grid, 'month').slice(1);
  const weekOffset = grid.columns[0]?.left ?? 0; // сетка может начинаться не с понедельника: сдвигаем узор выходных
  return (
    <>
      {days && (
        <>
          {/* выходные — серым, праздники так же; линии дней поверх */}
          <span className="absolute inset-0" style={{ background: `repeating-linear-gradient(to right, transparent 0 ${5 * grid.dayWidth}px, var(--color-ground) ${5 * grid.dayWidth}px ${7 * grid.dayWidth}px)`, backgroundPosition: `${weekOffset}px 0` }} />
          {holidays.map((d) => <span key={d} className="absolute top-0 bottom-0 bg-gray-100" style={{ left: d * grid.dayWidth, width: grid.dayWidth }} />)}
          <span className="absolute inset-0" style={{ background: `repeating-linear-gradient(to right, ${DAY_LINE} 0 1px, transparent 1px ${grid.dayWidth}px)` }} />
        </>
      )}
      {grid.columns.filter((c) => c.left >= 0).map((c) => (
        <span key={c.monday} className="absolute top-0 bottom-0 border-l border-gray-200" style={{ left: c.left }} />
      ))}
      {months.map((m) => (
        <span key={m.key} className="absolute top-0 bottom-0 border-l border-gray-400" style={{ left: m.left }} />
      ))}
      {/* подсветка задачи или события на всей сетке: полоса по датам под барами */}
      {spot && <span data-spot-band className="absolute top-0 bottom-0 bg-amber-300/30 border-x-2 border-amber-400/80 pointer-events-none" style={{ left: spot.left, width: spot.width }} />}
      {/* сегодня — поверх баров, слегка их затеняя; мышь не ловит */}
      {todayX !== null && <span className="absolute top-0 bottom-0 bg-red-500/15 z-[6] pointer-events-none" style={{ left: todayX, width: grid.dayWidth }} />}
    </>
  );
}
